import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { ViewListItem, ViewEntityType, Filter, Sort } from '@/types/api'
import { areFiltersEqual, areSortsEqual } from '@/lib/filter-utils'

/**
 * Working state for a specific view - tracks unsaved changes
 */
interface WorkingState {
  /** Current working filters (may differ from saved view) */
  filters: Filter[]
  /** Current working sorts (may differ from saved view) */
  sorts: Sort[]
  /** Current search term */
  searchTerm: string
  /** Base filters from the saved view (for comparison) */
  baseFilters: Filter[]
  /** Base sorts from the saved view (for comparison) */
  baseSorts: Sort[]
  /** Whether there are pending column changes (order, visibility, sizing) */
  hasPendingColumnChanges: boolean
}

/**
 * Create empty working state
 */
function createEmptyWorkingState(): WorkingState {
  return {
    filters: [],
    sorts: [],
    searchTerm: '',
    baseFilters: [],
    baseSorts: [],
    hasPendingColumnChanges: false,
  }
}

interface ViewsState {
  // Views per entity type
  viewsByEntity: Record<ViewEntityType, ViewListItem[]>

  // Current active view ID per entity type
  activeViewId: Record<ViewEntityType, string | null>

  // Working state per view (keyed by viewId)
  workingStateByView: Record<string, WorkingState>

  // Loading states
  isLoading: Record<ViewEntityType, boolean>

  // Actions - Views
  setViews: (entityType: ViewEntityType, views: ViewListItem[]) => void
  setActiveViewId: (entityType: ViewEntityType, viewId: string | null) => void
  setLoading: (entityType: ViewEntityType, loading: boolean) => void
  getDefaultViewId: (entityType: ViewEntityType) => string | null
  clearViews: (entityType: ViewEntityType) => void

  // Actions - Working State
  initWorkingState: (
    viewId: string,
    baseFilters: Filter[],
    baseSorts: Sort[]
  ) => void
  setWorkingFilters: (viewId: string, filters: Filter[]) => void
  setWorkingSorts: (viewId: string, sorts: Sort[]) => void
  setWorkingSearchTerm: (viewId: string, searchTerm: string) => void
  setHasPendingColumnChanges: (viewId: string, pending: boolean) => void
  resetWorkingState: (viewId: string) => void
  hasUnsavedChanges: (viewId: string) => boolean
  getWorkingState: (viewId: string) => WorkingState | undefined
}

export const useViewsStore = create<ViewsState>()(
  devtools(
    (set, get) => ({
      viewsByEntity: {
        COMPANY: [],
        FUND: [],
        CONTACT: [],
      },
      activeViewId: {
        COMPANY: null,
        FUND: null,
        CONTACT: null,
      },
      workingStateByView: {},
      isLoading: {
        COMPANY: false,
        FUND: false,
        CONTACT: false,
      },

      // View actions
      setViews: (entityType, views) =>
        set((state) => ({
          viewsByEntity: {
            ...state.viewsByEntity,
            [entityType]: views,
          },
        })),

      setActiveViewId: (entityType, viewId) =>
        set((state) => ({
          activeViewId: {
            ...state.activeViewId,
            [entityType]: viewId,
          },
        })),

      setLoading: (entityType, loading) =>
        set((state) => ({
          isLoading: {
            ...state.isLoading,
            [entityType]: loading,
          },
        })),

      getDefaultViewId: (entityType) => {
        const views = get().viewsByEntity[entityType]
        // Find the default view, or fall back to first view
        const defaultView = views.find((v) => v.isDefault) || views[0]
        return defaultView?.id ?? null
      },

      clearViews: (entityType) =>
        set((state) => ({
          viewsByEntity: {
            ...state.viewsByEntity,
            [entityType]: [],
          },
          activeViewId: {
            ...state.activeViewId,
            [entityType]: null,
          },
        })),

      // Working state actions
      initWorkingState: (viewId, baseFilters, baseSorts) =>
        set((state) => {
          // Only initialize if not already present or if base has changed
          const existing = state.workingStateByView[viewId]
          if (
            existing &&
            areFiltersEqual(existing.baseFilters, baseFilters) &&
            areSortsEqual(
              existing.baseSorts.map((s) => ({
                field: s.field,
                direction: s.direction,
              })),
              baseSorts.map((s) => ({ field: s.field, direction: s.direction }))
            )
          ) {
            return state
          }

          return {
            workingStateByView: {
              ...state.workingStateByView,
              [viewId]: {
                filters: baseFilters,
                sorts: baseSorts,
                searchTerm: existing?.searchTerm ?? '',
                baseFilters,
                baseSorts,
                hasPendingColumnChanges: false,
              },
            },
          }
        }),

      setWorkingFilters: (viewId, filters) =>
        set((state) => {
          const existing =
            state.workingStateByView[viewId] ?? createEmptyWorkingState()
          return {
            workingStateByView: {
              ...state.workingStateByView,
              [viewId]: {
                ...existing,
                filters,
              },
            },
          }
        }),

      setWorkingSorts: (viewId, sorts) =>
        set((state) => {
          const existing =
            state.workingStateByView[viewId] ?? createEmptyWorkingState()
          return {
            workingStateByView: {
              ...state.workingStateByView,
              [viewId]: {
                ...existing,
                sorts,
              },
            },
          }
        }),

      setWorkingSearchTerm: (viewId, searchTerm) =>
        set((state) => {
          const existing =
            state.workingStateByView[viewId] ?? createEmptyWorkingState()
          return {
            workingStateByView: {
              ...state.workingStateByView,
              [viewId]: {
                ...existing,
                searchTerm,
              },
            },
          }
        }),

      setHasPendingColumnChanges: (viewId, pending) =>
        set((state) => {
          const existing =
            state.workingStateByView[viewId] ?? createEmptyWorkingState()
          return {
            workingStateByView: {
              ...state.workingStateByView,
              [viewId]: {
                ...existing,
                hasPendingColumnChanges: pending,
              },
            },
          }
        }),

      resetWorkingState: (viewId) =>
        set((state) => {
          const existing = state.workingStateByView[viewId]
          if (!existing) return state

          return {
            workingStateByView: {
              ...state.workingStateByView,
              [viewId]: {
                ...existing,
                filters: existing.baseFilters,
                sorts: existing.baseSorts,
                searchTerm: '',
                hasPendingColumnChanges: false,
              },
            },
          }
        }),

      hasUnsavedChanges: (viewId) => {
        const state = get().workingStateByView[viewId]
        if (!state) return false

        // Check if filters differ from base
        if (!areFiltersEqual(state.filters, state.baseFilters)) return true

        // Check if sorts differ from base
        if (
          !areSortsEqual(
            state.sorts.map((s) => ({
              field: s.field,
              direction: s.direction,
            })),
            state.baseSorts.map((s) => ({
              field: s.field,
              direction: s.direction,
            }))
          )
        )
          return true

        // Check if columns have pending changes
        if (state.hasPendingColumnChanges) return true

        // Search term doesn't count as unsaved change (not saved to view)
        return false
      },

      getWorkingState: (viewId) => get().workingStateByView[viewId],
    }),
    {
      name: 'views-store',
    }
  )
)

// Selectors
export const selectViews =
  (entityType: ViewEntityType) => (state: ViewsState) =>
    state.viewsByEntity[entityType]

export const selectActiveViewId =
  (entityType: ViewEntityType) => (state: ViewsState) =>
    state.activeViewId[entityType]

export const selectIsLoading =
  (entityType: ViewEntityType) => (state: ViewsState) =>
    state.isLoading[entityType]

export const selectWorkingState = (viewId: string) => (state: ViewsState) =>
  state.workingStateByView[viewId]

export const selectWorkingFilters = (viewId: string) => (state: ViewsState) =>
  state.workingStateByView[viewId]?.filters ?? []

export const selectWorkingSorts = (viewId: string) => (state: ViewsState) =>
  state.workingStateByView[viewId]?.sorts ?? []

export const selectWorkingSearchTerm =
  (viewId: string) => (state: ViewsState) =>
    state.workingStateByView[viewId]?.searchTerm ?? ''

export type { WorkingState }
